/**
 * Idempotency utilities for preventing duplicate operations
 */

/**
 * Generate an idempotency key for order creation
 * Uses user ID and cart contents to create a unique key
 */
export function generateOrderIdempotencyKey(userId: string, cartHash: string): string {
  return `order_${userId}_${cartHash}`;
}

/**
 * SHA-256 hash function using the Web Crypto API
 */
async function sha256Hash(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a hash of cart contents for idempotency
 * Creates a deterministic SHA-256 hash based on cart items
 */
export async function hashCart(cart: {
  items: Array<{
    type: string;
    itemId: string;
    quantity: number;
    price: number;
    variant?: { data?: Record<string, unknown> };
  }>;
  total: number;
}): Promise<string> {
  // Sort items to ensure consistent ordering
  const sortedItems = [...cart.items].sort((a, b) => {
    if (a.type !== b.type) return a.type.localeCompare(b.type);
    if (a.itemId !== b.itemId) return a.itemId.localeCompare(b.itemId);
    return 0;
  });

  // Create a string representation of the cart
  const cartString = sortedItems
    .map((item) => {
      const variantStr = item.variant?.data
        ? JSON.stringify(item.variant.data, Object.keys(item.variant.data).sort())
        : '';
      return `${item.type}:${item.itemId}:${item.quantity}:${item.price}:${variantStr}`;
    })
    .join('|');

  // Add total to the hash
  const fullString = `${cartString}|total:${cart.total}`;

  return sha256Hash(fullString);
}

/**
 * Store idempotency key in session storage
 */
export function storeIdempotencyKey(key: string, orderId: string): void {
  try {
    const storage = window.sessionStorage;
    const data = {
      orderId,
      timestamp: Date.now(),
    };
    storage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to store idempotency key:', error);
  }
}

/**
 * Retrieve order ID from idempotency key
 * Returns null if key not found or expired (older than 1 hour)
 */
export function getOrderIdFromIdempotencyKey(key: string): string | null {
  try {
    const storage = window.sessionStorage;
    const item = storage.getItem(key);
    if (!item) return null;

    const data = JSON.parse(item);
    const age = Date.now() - data.timestamp;
    const ONE_HOUR = 60 * 60 * 1000;

    // Expire keys older than 1 hour
    if (age > ONE_HOUR) {
      storage.removeItem(key);
      return null;
    }

    return data.orderId;
  } catch (error) {
    console.warn('Failed to retrieve idempotency key:', error);
    return null;
  }
}

/**
 * Clear idempotency key from storage
 */
export function clearIdempotencyKey(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear idempotency key:', error);
  }
}
